import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Assignment,
  Block,
  Business,
  Cancel,
  CheckCircle,
  Person,
  Schedule,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetBundleDetailAssignmentsQuery } from '../store/hooks';
import { formatDateTime } from '../utils/formatting';

import { LoadingState } from './loading-state';

interface BundleAssignmentsSectionProps {
  bundleId: string;
}

const STATUS_OPTIONS = ['all', 'active', 'pending', 'canceled', 'expired'] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'active':
      return <CheckCircle fontSize="small" className="text-green-600" />;
    case 'pending':
      return <Schedule fontSize="small" className="text-yellow-600" />;
    case 'canceled':
      return <Cancel fontSize="small" className="text-red-600" />;
    case 'expired':
      return <Block fontSize="small" className="text-gray-500" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
};

const getSourceLabel = (source: string): string => {
  switch (source) {
    case 'stripe':
      return 'Stripe';
    case 'manual':
      return 'Manual';
    case 'trial':
      return 'Trial';
    default:
      return source;
  }
};

export function BundleAssignmentsSection({ bundleId }: BundleAssignmentsSectionProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const perPage = 20;

  const {
    data: assignmentsData,
    isLoading,
  } = useGetBundleDetailAssignmentsQuery({
    bundleId,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page,
    perPage,
  });

  const assignments = assignmentsData?.items || [];
  const totalPages = assignmentsData?.total_pages || 1;
  const total = assignmentsData?.total || 0;

  const handleStatusChange = (newStatus: StatusFilter) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const formatEffectiveDates = (from: string, to?: string | null) => {
    const fromDate = formatDateTime(new Date(from));
    if (to) {
      return `${fromDate} \u2192 ${formatDateTime(new Date(to))}`;
    }
    return `${fromDate} \u2192`;
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div>
            <Typography variant="h6">Assignments</Typography>
            <Typography variant="body2" className="text-gray-500">
              Organizations and users with this bundle assigned
              {total > 0 && ` (${total} total)`}
            </Typography>
          </div>
        </div>

        <Box className="mb-4">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => handleStatusChange(e.target.value as StatusFilter)}
            >
              {STATUS_OPTIONS.map(status => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <LoadingState message="Loading assignments..." />
        ) : assignments.length === 0 ? (
          <div className="text-center py-8">
            <Assignment className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <Typography variant="body1" className="text-gray-500">
              No assignments found
            </Typography>
            <Typography variant="body2" className="text-gray-400 mt-1">
              {statusFilter !== 'all'
                ? `No ${statusFilter} assignments for this bundle`
                : 'This bundle has not been assigned to any organizations or users'}
            </Typography>
          </div>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Effective</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map(assignment => (
                  <TableRow key={assignment.assignment_id}>
                    <TableCell>
                      <Link
                        component="button"
                        onClick={() =>
                          navigate(`/admin-v2/organization/${assignment.organization_id}`)
                        }
                        className="text-left font-medium"
                        underline="hover"
                      >
                        {assignment.organization_name || assignment.organization_id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {assignment.user_id ? (
                          <>
                            <Person fontSize="small" className="text-gray-400" />
                            <Link
                              component="button"
                              onClick={() =>
                                navigate(`/admin-v2/providers/${assignment.user_id}`)
                              }
                              underline="hover"
                              className="text-sm"
                            >
                              {assignment.user_name || assignment.user_id}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Business fontSize="small" className="text-gray-400" />
                            <span className="text-sm text-gray-600">Organization</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(assignment.status) || undefined}
                        label={assignment.status}
                        size="small"
                        color={getStatusColor(assignment.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getSourceLabel(assignment.source)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {formatEffectiveDates(
                          assignment.effective_from,
                          assignment.effective_to
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <Box className="flex justify-center mt-4">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
